import dayjs, { Dayjs } from "dayjs"
import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { PageControlItem } from "../components/atoms/DBT/PageControl"
import CompoundResponse, {
  CompoundResponseValue,
} from "../components/atoms/CompoundResponse"
import DBTScoreIcon, { DBTScore } from "../components/atoms/DBT/DBTScoreIcon"
import DBTTimeIntervalIcon from "../components/atoms/DBT/DBTTimeIntervalIcon"
import DBTYesNoIcon, {
  DBTYesNoValue,
} from "../components/atoms/DBT/DBTYesNoIcon"
import {
  GenericTableCellValue,
  GenericTableColumn,
  GenericTableProps,
  GenericTableRow,
  TableColumnKey,
} from "../components/molecules/GenericTable"
import { useGetAssessmentQuestionCustomPromptsByClientIdQuery } from "../generated/graphql"
import {
  DBTDIARYCARDASSESSMENT_ID,
  DBTDIARYCARDWEEKLYCHECKIN_ID,
} from "../utils/constants"
import {
  AssessmentTake,
  QuestionProps,
  SwitchboardOption,
  SwitchBoardQuestionResult,
} from "../utils/types"
import useAssessment from "./useAssessment"
import {
  buildWeekPageItems,
  countCompletedTakesInWindow,
  getDailyDiaryWindow,
  getDiaryWindowFromEndKey,
} from "./dbt/dbtDiaryOverviewDates"
import { useGraphQLClient } from "./useGraphQLClient"

const EMPTY_ASSESSMENT_TAKES: AssessmentTake[] = []

/**
 * Normalizes an hour value to be within the range of 0 to 23.75.
 * @param value - The hour value to normalize.
 * @returns The normalized hour value.
 */
const normalizeHour = (value: number): number => {
  const wrapped = value < 0 ? 0 : value
  return wrapped > 23.75 ? 23.75 : wrapped
}

/**
 * Parses a variety of hour formats (decimal, HH:mm, HH:mm AM/PM) into decimal hours.
 */
const parseHourValue = (value: string | number | undefined | null): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (value === undefined || value === null) {
    return 0
  }

  const sanitized = String(value)
    .trim()
    .replace(/\u202f/gi, " ") // normalize narrow no-break spaces seen in inputs like "10:45 AM"

  const numeric = Number(sanitized)
  if (!Number.isNaN(numeric)) {
    return numeric
  }

  const meridianMatch = sanitized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (meridianMatch) {
    let hours = parseInt(meridianMatch[1], 10) % 12
    const minutes = parseInt(meridianMatch[2], 10)
    const meridian = meridianMatch[3].toLowerCase()
    if (meridian === "pm") {
      hours += 12
    }
    if (meridian === "am" && parseInt(meridianMatch[1], 10) === 12) {
      hours = 0
    }
    return hours + minutes / 60
  }

  const twentyFourHourMatch = sanitized.match(/^(\d{1,2}):(\d{2})$/)
  if (twentyFourHourMatch) {
    const hours = parseInt(twentyFourHourMatch[1], 10)
    const minutes = parseInt(twentyFourHourMatch[2], 10)
    return hours + minutes / 60
  }

  console.warn("Unable to parse hour value", value)
  return 0
}

/** Formats a given hour value into an ISO 8601 date-time string.
 * @param baseDate - The base date to use for formatting.
 * @param hourValue - The hour value to format.
 * @returns The formatted ISO 8601 date-time string.
 */
const formatHour = (baseDate: Dayjs, hourValue: number): string => {
  const wholeHours = Math.floor(hourValue)
  const minutes = Math.round((hourValue - wholeHours) * 60)
  return baseDate
    .hour(wholeHours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .format("YYYY-MM-DDTHH:mm")
}

interface DayColumn {
  key: string
  label: string
}

function getDayOrderForColumns(endingWith: dayjs.Dayjs): DayColumn[] {
  const labelForDay = (dayIndex: number): string => {
    switch (dayIndex) {
      case 0:
        return "Sun"
      case 1:
        return "M"
      case 2:
        return "T"
      case 3:
        return "W"
      case 4:
        return "Th"
      case 5:
        return "F"
      case 6:
        return "Sat"
      default:
        return ""
    }
  }

  const orderedDays: DayColumn[] = []

  for (let offset = 6; offset >= 0; offset--) {
    const day = endingWith.subtract(offset, "day")
    orderedDays.push({
      key: day.toISOString(),
      label: labelForDay(day.day()),
    })
  }

  return orderedDays
}

const createYesNoCell = (value: boolean): GenericTableCellValue => ({
  type: "YesNo",
  value: value ? "Yes" : "No",
})

const createYesNoBooleanCell = (value: boolean): GenericTableCellValue => ({
  type: "YesNoBoolean",
  value: value ? "Yes" : "No",
})

const createMealCell = (value: DBTScore): GenericTableCellValue => ({
  type: "mealIcon",
  value,
})

const createTimeCell = (start: string, end: string): GenericTableCellValue => ({
  type: "timeInterval",
  value: [start, end],
})

const buildSleepInterval = (
  dayDate: Dayjs,
  bedtimeHour: string | number,
  waketimeHour: string | number
): [string, string] => {
  const normalizedBed = normalizeHour(parseHourValue(bedtimeHour))
  const normalizedWake = normalizeHour(parseHourValue(waketimeHour))
  const startIso = formatHour(dayDate, normalizedBed)
  const wakeAfterMidnight = normalizedWake <= normalizedBed
  const wakeDate = wakeAfterMidnight ? dayDate.add(1, "day") : dayDate
  const endIso = formatHour(wakeDate, normalizedWake)
  return [startIso, endIso]
}

const createCompoundCell = (
  value: CompoundResponseValue,
  occurred: boolean
): GenericTableCellValue => ({
  type: "compound",
  value: [value, occurred ? "Yes" : "No"],
})

function buildWeekdayTableColumns(
  from: dayjs.Dayjs,
  topLeftLabel: string = "",
  titleRenderTypeOverride?: string
): GenericTableColumn[] {
  return [
    {
      key: "questionText",
      displayText: topLeftLabel,
      width: 239,
      align: "left",
      titleRenderTypeOverride,
    },
    ...getDayOrderForColumns(from).map((dayColumn) => ({
      key: dayColumn.key,
      displayText: dayColumn.label,
      minWidth: 32,
      align: "center" as const,
    })),
  ]
}

interface RowBuilderConfig {
  title: string
  subtitle: string
  type: "compound" | "yesno" | "mealIcon" | "timeInterval"
  pageID: string
  customPromptId?: string | null
}

const UrgesAndBehaviorTableRowConfig: RowBuilderConfig[] = [
  {
    title: "Self-Harm",
    subtitle: "Urge / Action",
    type: "compound",
    pageID: "9836c57b-66ba-49a9-b584-e482c4c2643f",
  },
  {
    title: "Suicidal",
    subtitle: "Urge / Action",
    type: "compound",
    pageID: "584717cd-cc1a-4c0d-b22e-f921f111ff81",
  },
  {
    title: "Taking Meds as Prescribed",
    subtitle: "",
    type: "yesno",
    pageID: "a1db3dfe-2010-4e59-80f0-514640669236",
  },
  {
    title: "Meals",
    subtitle: "",
    type: "mealIcon",
    pageID: "b404e426-fbb9-4df2-9e15-0ed229650a2a",
  },
  {
    title: "Sleep",
    subtitle: "Sleep / Wake",
    type: "timeInterval",
    pageID: "3e647a39-0f9b-4463-8860-68b58372e529",
  },
]

const EmotionsTableRowQuestionIDs = [
  "e47bddad-fc90-48e6-9513-a86e47ca7dc5",
  "32af38c0-b709-4724-ab96-469a3323f6e2",
  "03a3ad30-4db3-4322-81c6-d945f220a2a7",
  "b198234f-67f5-49b4-bbb2-e1bbfab8f722",
  "6311828f-63a2-47db-89d0-e8b09393cf11",
  "1a25a179-bcb5-4052-8cde-174fd0424e2b",
  "404cb795-f39b-4689-a0f7-e560afbe56de",
]

const UrgeQuestionIds = [
  "e336c167-9e5c-4020-a2e3-96cbf494e946",
  "2fa6ff6c-cef7-4bb3-a5cf-d967dc7e34c0",
  "4ef2062a-a88b-494c-9e6f-b4c780dc5919",
  "73de75e4-1d57-4177-8e09-0c1fc73b5afe",
  "28fba1ff-9792-49f1-a6a7-f43e2063e7ea",
  "84adc27a-d92b-45aa-a911-cbbe5328d159",
]

// Custom urge prompt questions (intensity questions) that can have
// client-specific text/headers via assessment_question_custom_prompt
const CustomUrgePromptQuestionIds: string[] = [
  "73de75e4-1d57-4177-8e09-0c1fc73b5afe",
  "28fba1ff-9792-49f1-a6a7-f43e2063e7ea",
  "84adc27a-d92b-45aa-a911-cbbe5328d159",
]

const SkillsTableRowOffsets: Record<string, number> = {
  Mindfulness: 1,
  "Distress Tolerance": 8,
  "Walking The Middle Path": 14,
  "Emotional Regulation": 19,
  "Interpersonal Effectiveness": 29,
}

const SkillsTableRowQuestionIDs: Record<string, string> = {
  Mindfulness: "ea1f5722-d97b-4f0b-b8ee-eac9c10ed234",
  "Distress Tolerance": "dc5c2483-9f7f-4d47-84a2-5c55b874b228",
  "Walking The Middle Path": "27cd3ce7-f17b-4040-a930-489252fef1ae",
  "Emotional Regulation": "756e148b-0d85-4f41-ae77-395146b4d0bb",
  "Interpersonal Effectiveness": "ca02be33-cc38-48a9-8876-9d9dbba39c0c",
}

const WeeklyAssessmentTableColumnQuestionIDs: Record<string, string> = {
  urge: "1d08672e-71d4-40ce-8c03-6fbb58e765df",
  misery: "2143740a-a4d8-443a-a9e0-320d0da65875",
  phone: "ce3c702a-2d28-4ca5-9689-b4d32f183e2e",
  completed: "none",
  goals: "f6df06f0-58ae-467b-8e7f-22c1d14e1132",
}

const weeklyAssessmentTableColumns: GenericTableColumn[] = [
  {
    key: "urge",
    displayText: "Urge to quit therapy",
    width: 100,
    align: "center",
  },
  {
    key: "misery",
    displayText: "Level of Misery",
    width: 100,
    align: "center",
  },
  {
    key: "phone",
    displayText: "Times used phone coaching",
    width: 100,
    align: "center",
  },
  {
    key: "completed",
    displayText: "Times completed diary card",
    width: 100,
    align: "center",
  },
  {
    key: "goals",
    displayText: "Life Worth Living Goals",
    width: 100,
    align: "center",
  },
]

/**
 * Renders a table cell based on the type of value provided.
 *
 * @param value - The cell value to render, containing a type discriminator and associated data
 * @returns A React element representing the rendered cell, or null if value is undefined
 *
 * @remarks
 * Supports the following cell types:
 * - `TitleSubtitle`: Renders a bold title with a smaller gray subtitle
 * - `compound`: Renders a CompoundResponse component with yes/no variant
 * - `title`: Renders text in bold
 * - `mealIcon`: Renders a DBTScoreIcon with meals-7 scale
 * - `emotionIcon`: Renders a DBTScoreIcon with emotions-6 scale
 * - `intensityIcon`: Renders a DBTScoreIcon with intensity-6 scale
 * - `YesNo`: Renders a DBTYesNoIcon with status palette
 * - `timeInterval`: Renders a DBTTimeIntervalIcon with horizontal orientation
 * - `YesNoBoolean`: Renders a DBTYesNoIcon with boolean palette
 * - default: Renders plain text in a span element
 */
function CellRenderFunction(
  value: GenericTableCellValue | undefined,
  useSplitTableLayout: boolean | undefined = false
) {
  if (!value) return null
  switch (value.type) {
    case "TitleSubtitle":
      return (
        <div>
          <div style={{ fontWeight: "bold", fontSize: 16, color: "#2F3B5A" }}>
            {(value.value as any).title}
          </div>
          <div style={{ fontSize: "smaller", color: "gray" }}>
            {(value.value as any).subtitle}
          </div>
        </div>
      )
    case "compound":
      return (
        <CompoundResponse
          value={(value.value as [CompoundResponseValue, string])[0]}
          variant={
            (value.value as [CompoundResponseValue, string])[1] === "Yes"
              ? "yes"
              : "no"
          }
        />
      )
    case "title":
      return (
        <strong style={{ fontSize: 16, color: "#2F3B5A" }}>
          {value.value as string}
        </strong>
      )
    case "mealIcon":
      return <DBTScoreIcon score={value.value as DBTScore} scale="meals-7" />
    case "overallSkillsRow":
      return (
        <DBTScoreIcon score={value.value as DBTScore} scale="intensity-8" />
      )
    case "emotionIcon":
      return <DBTScoreIcon score={value.value as DBTScore} scale="emotions-6" />
    case "intensityIcon":
      return (
        <DBTScoreIcon score={value.value as DBTScore} scale="intensity-6" />
      )
    case "YesNo":
      return (
        <DBTYesNoIcon
          value={(value.value as string).toLowerCase() as DBTYesNoValue}
          palette="status"
        />
      )
    case "timeInterval":
      return (
        <DBTTimeIntervalIcon
          start={(value.value as [string, string])[0]}
          end={(value.value as [string, string])[1]}
          orientation={useSplitTableLayout ? "horizontal" : "vertical"}
        />
      )
    case "YesNoBoolean":
      return (
        <DBTYesNoIcon
          value={(value.value as string).toLowerCase() as DBTYesNoValue}
          palette="boolean"
        />
      )
    case "titleWithColon":
      let titleParts = (value.value as string).split(":")
      if (titleParts.length < 2) {
        return <strong>{titleParts[0]}</strong>
      }
      return (
        <p>
          <strong>{titleParts[0]}:</strong> {titleParts[1]}
        </p>
      )
    case "goals":
      // value is a stringified array of strings
      const goals = JSON.parse(value.value as string) as string[]
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "9px",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          {goals.map((goal, index) => (
            <strong key={index}>{goal.trim()}</strong>
          ))}
        </div>
      )
    case "skillTitle":
      return (
        <strong
          style={{
            color: "#4F66AA",
            marginLeft: 8,
            fontSize: 18,
          }}
        >
          {value.value as string}
        </strong>
      )
    default:
      return <span>{value.value as string}</span>
  }
}

/**
 * useDBTDiaryOverview is a custom hook that manages the state and data fetching
 * for the DBT Diary Card overview page.
 */
function useDBTDiaryOverview(clientId: string) {
  const {
    getPopulatedAssessment,
    getAssessmentResults,
    getAssessmentQuestionsWithIdsIn,
  } = useAssessment()

  const graphQLClient = useGraphQLClient()
  const { data: customPromptData } =
    useGetAssessmentQuestionCustomPromptsByClientIdQuery(graphQLClient, {
      clientId: clientId,
    })

  const [selectedWeekKey, setSelectedWeekKey] = useState<string | null>(null)

  // Freeze the window on mount so every table stays within the same calendar
  // context while the practitioner pages through this overview.
  const initialDailyWindowEnd = useMemo(() => dayjs().startOf("day"), [])

  const assessmentQuery = useQuery({
    queryKey: ["DBTDiaryOverviewAssessment"],
    queryFn: () => getPopulatedAssessment(DBTDIARYCARDASSESSMENT_ID),
  })
  const weeklyAssessmentsQuery = useQuery({
    queryKey: ["DBTDiaryOverviewWeeklyAssessments", clientId],
    queryFn: async () => {
      const assessments = await getAssessmentResults(
        DBTDIARYCARDWEEKLYCHECKIN_ID,
        clientId
      )
      return assessments.sort((a, b) =>
        dayjs(a.completedAt).isBefore(dayjs(b.completedAt)) ? 1 : -1
      )
    },
  })
  const skillsQuestionsQuery = useQuery({
    queryKey: ["DBTDiaryOverviewSkillsQuestions"],
    queryFn: () =>
      getAssessmentQuestionsWithIdsIn(Object.values(SkillsTableRowQuestionIDs)),
  })

  const DBTAssessment = assessmentQuery.data
  const weeklyAssessments =
    weeklyAssessmentsQuery.data ?? EMPTY_ASSESSMENT_TAKES
  const currentWindow = useMemo(
    () => getDailyDiaryWindow(initialDailyWindowEnd, 0),
    [initialDailyWindowEnd]
  )
  const currentWeekKey = currentWindow.end.format("YYYY-MM-DD")
  const weekPageItems = useMemo<PageControlItem<string>[]>(
    () =>
      buildWeekPageItems(
        initialDailyWindowEnd,
        weeklyAssessments.flatMap((assessment) =>
          assessment.completedAt ? [assessment.completedAt] : []
        )
      ),
    [initialDailyWindowEnd, weeklyAssessments]
  )
  const activeWeekKey = selectedWeekKey ?? currentWeekKey
  const selectedWeekPage = weekPageItems.find(
    (page) => page.value === activeWeekKey
  )
  // Defines the seven-day diary period shown by the overview: use the selected
  // page's end date for historical weeks, or the frozen current window when
  // viewing the current week.
  const dailyWindow = useMemo(
    () =>
      selectedWeekPage && selectedWeekPage.value !== currentWeekKey
        ? getDiaryWindowFromEndKey(selectedWeekPage.value)
        : currentWindow,
    [currentWeekKey, currentWindow, selectedWeekPage]
  )
  const { start: dailyWindowStart, end: dailyWindowEnd } = dailyWindow
  const dailyWindowStartIso = dailyWindowStart.startOf("day").toISOString()
  const dailyWindowEndIso = dailyWindowEnd.endOf("day").toISOString()
  const dailyAssessmentsQuery = useQuery({
    queryKey: [
      "DBTDiaryOverviewDailyAssessments",
      clientId,
      dailyWindowStartIso,
      dailyWindowEndIso,
    ],
    queryFn: () =>
      getAssessmentResults(
        DBTDIARYCARDASSESSMENT_ID,
        clientId,
        dailyWindowStartIso,
        dailyWindowEndIso
      ),
  })
  const assessmentTakes = dailyAssessmentsQuery.data ?? EMPTY_ASSESSMENT_TAKES
  const skillsQuestionsMap = useMemo(
    () =>
      (skillsQuestionsQuery.data ?? []).reduce((map, question) => {
        map[question.id] = question
        return map
      }, {} as Record<string, QuestionProps>),
    [skillsQuestionsQuery.data]
  )
  // Weekly check-ins are matched to the selected seven-day period rather than
  // being used to define that period. When more than one check-in exists in
  // a period, keep the latest completion for the table and date label.
  const selectedWeeklyAssessment = weeklyAssessments
    .filter((assessment) => {
      if (!assessment.completedAt) return false
      const completedDay = dayjs(assessment.completedAt).startOf("day")
      return (
        !completedDay.isBefore(dailyWindow.start, "day") &&
        !completedDay.isAfter(dailyWindow.end, "day")
      )
    })
    .reduce<AssessmentTake | undefined>((latest, assessment) => {
      if (
        !latest ||
        dayjs(assessment.completedAt).isAfter(dayjs(latest.completedAt))
      ) {
        return assessment
      }
      return latest
    }, undefined)

  useEffect(() => {
    setSelectedWeekKey(null)
  }, [clientId])

  // Build dynamic row configs for any enabled custom urges based on
  // assessment_question_custom_prompt entries that are actually referenced
  // by this client's assessment answers.
  const customUrgesRowConfig = useMemo<RowBuilderConfig[]>(() => {
    if (
      !DBTAssessment ||
      !customPromptData ||
      assessmentTakes.length === 0 ||
      !dailyWindowEnd
    ) {
      return []
    }

    const weekEnd = dailyWindowEnd
    const weekStart = dailyWindowStart

    const takesInWeek = assessmentTakes.filter((take) => {
      const completed = dayjs(take.completedAt)
      if (completed.isBefore(weekStart, "day")) return false
      if (completed.isAfter(weekEnd, "day")) return false
      return true
    })

    const configs: RowBuilderConfig[] = []

    const promptsById = new Map<
      string,
      (typeof customPromptData.assessment_question_custom_prompt)[number]
    >()
    customPromptData.assessment_question_custom_prompt.forEach((prompt) => {
      promptsById.set(String(prompt.id), prompt)
    })

    const questionsById = new Map<string, QuestionProps>()
    DBTAssessment.pages.forEach((page) => {
      page.questions.forEach((question) => {
        questionsById.set(question.id, question)
      })
    })

    // Preserve a stable ordering by walking the known custom-urge question
    // IDs and, for each, checking if we have a prompt actually used in
    // this client's data during the rolling daily window.
    CustomUrgePromptQuestionIds.forEach((questionId, index) => {
      const matchingResult = takesInWeek
        .flatMap((take) => take.results)
        .find(
          (result) => result.customPromptId && result.questionId === questionId
        )

      if (!matchingResult || !matchingResult.customPromptId) {
        return
      }

      const prompt = promptsById.get(matchingResult.customPromptId)
      const question = questionsById.get(questionId)

      if (!prompt || !question) {
        return
      }

      // If the prompt was soft-deleted before the start of this week,
      // treat it as inactive for this week. Otherwise (deleted during or
      // after this week, or not deleted at all) we still show it so that
      // previous weeks continue to display their historical custom-urge
      // results.
      if (prompt.deleted_at) {
        const deletedAt = dayjs(prompt.deleted_at)
        if (deletedAt.isBefore(weekStart, "day")) {
          return
        }
      }

      const header = (prompt.header ?? "").trim()
      const text = (prompt.text ?? "").trim()

      const title = header || text || `Custom Urge ${index + 1}`

      configs.push({
        title,
        subtitle: "Urge / Action",
        type: "compound",
        pageID: question.pageId,
        customPromptId: String(prompt.id),
      })
    })

    return configs
  }, [
    DBTAssessment,
    assessmentTakes,
    customPromptData,
    dailyWindowEnd,
    dailyWindowStart,
  ])

  /**
   * Generates the data for the Weekly Check-In table.
   * The columns are based on the WeeklyAssessmentTableColumnQuestionIDs mapping.
   * If there are no assessments or the DBTAssessment is not loaded, it returns empty columns and rows.
   */
  const WeeklyCheckinTableData = useMemo<GenericTableProps>(() => {
    if (!DBTAssessment || !selectedWeeklyAssessment) {
      return { columns: [], rows: [], renderCell: () => null }
    }
    // There is only one row for the weekly check-in table
    let rows: GenericTableRow[] = []
    let rowValues: Record<TableColumnKey, GenericTableCellValue> = {}
    for (const [columnKey, questionId] of Object.entries(
      WeeklyAssessmentTableColumnQuestionIDs
    )) {
      if (questionId === "none") {
        if (dailyAssessmentsQuery.isSuccess) {
          rowValues[columnKey] = {
            type: "intensityIcon",
            value: countCompletedTakesInWindow(assessmentTakes, dailyWindow),
          }
        }
      } else {
        // Find the answer for the selected weekly assessment.
        const result = selectedWeeklyAssessment.results.find(
          (r) => r.questionId === questionId
        )
        if (result) {
          rowValues[columnKey] = {
            type:
              questionId === "f6df06f0-58ae-467b-8e7f-22c1d14e1132"
                ? "goals"
                : "intensityIcon",
            value: result.result as string,
          }
        }
      }
    }
    rows.push({
      id: selectedWeeklyAssessment.takeId ?? "weeklyCheckin",
      values: rowValues,
    })
    const columns = weeklyAssessmentTableColumns
    return { columns, rows, renderCell: CellRenderFunction }
  }, [
    DBTAssessment,
    selectedWeeklyAssessment,
    assessmentTakes,
    dailyWindow,
    dailyAssessmentsQuery.isSuccess,
  ])

  /**
   * Generates the columns for the daily diary overview table.
   * It builds columns for today and the six preceding calendar days.
   * If the DBTAssessment is not loaded, it returns an empty array.
   */
  const ColumnDataForDailyDiary = useCallback(
    (
      topLeftLabel: string = "",
      titleRenderTypeOverride?: string
    ): GenericTableColumn[] => {
      if (!DBTAssessment) {
        return []
      }
      return buildWeekdayTableColumns(
        dailyWindowEnd,
        topLeftLabel,
        titleRenderTypeOverride
      )
    },
    [DBTAssessment, dailyWindowEnd]
  )

  /**
   * Generates the data for the Urges and Behavior table.
   * It creates rows based on the UrgesAndBehaviorTableRowConfig and the rolling daily window.
   * If the DBTAssessment or assessmentTakes are not loaded, it returns empty columns and rows.
   */
  const UrgesAndBehaviorTableData = useMemo<GenericTableProps>(() => {
    if (!DBTAssessment) {
      return { columns: [], rows: [], renderCell: () => null }
    }
    const columns = ColumnDataForDailyDiary("")
    const allRowConfigs: RowBuilderConfig[] = [
      ...UrgesAndBehaviorTableRowConfig,
      ...customUrgesRowConfig,
    ]

    const rows: GenericTableRow[] = allRowConfigs.map((config, rowIndex) => {
      const questions = DBTAssessment.pages.find(
        (page) => page.id === config.pageID
      )?.questions

      let values: Record<TableColumnKey, GenericTableCellValue> = {
        questionText: {
          type: "TitleSubtitle",
          value: { title: config.title, subtitle: config.subtitle },
        },
      }
      if (questions) {
        // for each day in the week, find the corresponding assessment take
        // get the questions on the page and find the results for those questions
        // map those results to the correct cell type
        const questionIds = questions.map((q) => q.id)
        const weekEnd = dailyWindowEnd
        getDayOrderForColumns(weekEnd).forEach((dayColumn) => {
          const dayDate = dayjs(dayColumn.key)
          const takeForDay = assessmentTakes.find((take) =>
            dayjs(take.completedAt).isSame(dayDate, "day")
          )
          if (takeForDay) {
            const questionResults = takeForDay.results.filter((result) => {
              if (!questionIds.includes(result.questionId)) {
                return false
              }

              if (config.customPromptId) {
                return result.customPromptId === config.customPromptId
              }

              return true
            })
            if (questionResults.length > 0) {
              switch (config.type) {
                case "compound":
                  if (questionResults.length < 2) {
                    console.warn(
                      `Expected 2 results for compound question, got ${questionResults.length}`,
                      "results:",
                      questionResults
                    )
                    values[dayColumn.key] = {
                      type: "title",
                      value: "Error",
                    }
                    break
                  }
                  const urgeQuestion = questionResults.find((q) =>
                    UrgeQuestionIds.includes(q.questionId)
                  )
                  const actionQuestion = questionResults.find(
                    (q) => !UrgeQuestionIds.includes(q.questionId)
                  )
                  values[dayColumn.key] = createCompoundCell(
                    urgeQuestion?.result as CompoundResponseValue,
                    actionQuestion?.result as boolean
                  )
                  break
                case "yesno":
                  const response = questionResults[0].result as boolean
                  values[dayColumn.key] = createYesNoCell(response)
                  break
                case "mealIcon":
                  const mealScore = questionResults[0].result as DBTScore
                  values[dayColumn.key] = createMealCell(mealScore)
                  break
                case "timeInterval":
                  if (questionResults.length !== 2) {
                    break
                  }
                  // Sort timeInterval questions by displayOrder to ensure correct bedtime/waketime order
                  const sortedTimeResults = questionResults
                    .map((result) => ({
                      result,
                      question: questions.find(
                        (q) => q.id === result.questionId
                      ),
                    }))
                    .filter((item) => item.question !== undefined)
                    .sort(
                      (a, b) =>
                        (a.question?.displayOrder ?? 0) -
                        (b.question?.displayOrder ?? 0)
                    )
                    .map((item) => item.result)
                  const bedtime = sortedTimeResults[0].result as string
                  const waketime = sortedTimeResults[1].result as string
                  const [startIso, endIso] = buildSleepInterval(
                    dayDate,
                    bedtime,
                    waketime
                  )
                  values[dayColumn.key] = createTimeCell(startIso, endIso)
                  break
                default:
                  values[dayColumn.key] = {
                    type: "title",
                    value: "N/A",
                  }
              }
            }
          }
        })
      }
      return {
        id: `row-${rowIndex}`,
        values,
      }
    })
    return { columns, rows, renderCell: CellRenderFunction }
  }, [
    DBTAssessment,
    assessmentTakes,
    ColumnDataForDailyDiary,
    customUrgesRowConfig,
    dailyWindowEnd,
  ])

  /**
   * Generates the data for the Emotions table.
   * It creates rows based on the EmotionsTableRowQuestionIDs and the rolling daily window.
   * If the DBTAssessment or assessmentTakes are not loaded, it returns empty columns and rows.
   */
  const EmotionsTableData = useMemo<GenericTableProps>(() => {
    if (!DBTAssessment) {
      return { columns: [], rows: [], renderCell: () => null }
    }
    const columns = ColumnDataForDailyDiary("")
    const rows: GenericTableRow[] = EmotionsTableRowQuestionIDs.map(
      (questionId, rowIndex) => {
        const question = DBTAssessment.pages
          .flatMap((page) => page.questions)
          .find((q) => q.id === questionId)
        const title = question?.properties?.subtitle ?? "Unknown Emotion"

        let values: Record<TableColumnKey, GenericTableCellValue> = {
          questionText: {
            type: "title",
            value: title,
          },
        }
        // For each day in the rolling window, find the corresponding
        // assessment take and emotion result.
        const weekEnd = dailyWindowEnd
        getDayOrderForColumns(weekEnd).forEach((dayColumn) => {
          const dayDate = dayjs(dayColumn.key)
          const takeForDay = assessmentTakes.find((take) =>
            dayjs(take.completedAt).isSame(dayDate, "day")
          )
          if (takeForDay) {
            const questionResult = takeForDay.results.find(
              (result) => result.questionId === questionId
            )
            if (questionResult) {
              values[dayColumn.key] = {
                type: "emotionIcon",
                value: String(questionResult.result),
              }
            }
          }
        })
        return {
          id: `row-${rowIndex}`,
          values,
        }
      }
    )

    return { columns, rows, renderCell: CellRenderFunction }
  }, [DBTAssessment, assessmentTakes, dailyWindowEnd, ColumnDataForDailyDiary])

  /**
   * Generates a combined, pivoted table for Urges & Behavior and Emotions.
   *
   * - Rows represent days of the week (M, T, W, Th, F, Sat, Sun)
   * - Columns after the first day label column represent the various
   *   Urges/Behavior metrics followed by Emotion metrics.
   * - This layout matches the split-table visual in the DBT diary designs.
   */
  const UrgesAndEmotionsSplitTableData = useMemo<GenericTableProps>(() => {
    if (
      UrgesAndBehaviorTableData.columns.length === 0 ||
      EmotionsTableData.columns.length === 0
    ) {
      return { columns: [], rows: [], renderCell: () => null }
    }

    // Day columns are all non-label columns from the existing urges table.
    const dayColumns = UrgesAndBehaviorTableData.columns.filter(
      (column) => column.key !== "questionText"
    )

    const columns: GenericTableColumn[] = []

    // First column: day label (M, T, W, ...)
    columns.push({
      key: "dayLabel",
      displayText: "",
      width: 56,
      align: "center",
    })

    const buildHeaderLabelFromCell = (
      cell: GenericTableCellValue | undefined
    ): string => {
      if (!cell) return ""
      if (cell.type === "TitleSubtitle") {
        const value = cell.value as { title?: string; subtitle?: string }
        if (value.subtitle) {
          return `${value.title ?? ""}\n${value.subtitle}`.trim()
        }
        if (value.title) {
          return value.title
        }
      }
      if (typeof cell.value === "string") {
        return cell.value
      }
      return String(cell.value ?? "")
    }

    // Urges & Behavior metric columns
    UrgesAndBehaviorTableData.rows.forEach((row, index) => {
      const headerCell = row.values["questionText"]
      columns.push({
        key: `u-${index}`,
        displayText: buildHeaderLabelFromCell(headerCell),
        align: "center",
        minWidth: 80,
        headerValue: headerCell,
      })
    })

    // Emotion metric columns
    EmotionsTableData.rows.forEach((row, index) => {
      const headerCell = row.values["questionText"]
      columns.push({
        key: `e-${index}`,
        displayText: buildHeaderLabelFromCell(headerCell),
        align: "center",
        minWidth: 64,
        headerValue: headerCell,
      })
    })

    const rows: GenericTableRow[] = dayColumns.map((dayColumn, dayIndex) => {
      const values: Record<TableColumnKey, GenericTableCellValue> = {
        dayLabel: {
          type: "title",
          value: dayColumn.displayText,
        },
      }

      // Fill in urges & behavior cells for this day
      UrgesAndBehaviorTableData.rows.forEach((row, index) => {
        const columnKey: TableColumnKey = `u-${index}`
        const sourceCell = row.values[dayColumn.key]
        if (sourceCell) {
          values[columnKey] = sourceCell
        }
      })

      // Fill in emotion cells for this day
      EmotionsTableData.rows.forEach((row, index) => {
        const columnKey: TableColumnKey = `e-${index}`
        const sourceCell = row.values[dayColumn.key]
        if (sourceCell) {
          values[columnKey] = sourceCell
        }
      })

      return {
        id: dayColumn.key || `day-${dayIndex}`,
        values,
      }
    })

    // Place split border after the last Urges & Behavior column.
    const splitAfterColumnIndex = 1 + UrgesAndBehaviorTableData.rows.length - 1 // account for day column

    return {
      columns,
      rows,
      renderCell: UrgesAndBehaviorTableData.renderCell,
      splitAfterColumnIndex,
    }
  }, [UrgesAndBehaviorTableData, EmotionsTableData])

  /**
   * Generates the data for the Overall Skills table.
   * It creates a single row with the overall skills results for the rolling daily window.
   * The columns are based on the total count of skills used each day.
   * If the DBTAssessment or assessmentTakes are not loaded, it returns empty columns and rows.
   */
  const OverallSkillsTableData = useMemo<GenericTableProps>(() => {
    if (!DBTAssessment) {
      return { columns: [], rows: [], renderCell: () => null }
    }
    const columns = ColumnDataForDailyDiary("")
    let rows: GenericTableRow[] = []
    // Count every skill used for each day in the rolling daily window.
    let values: Record<TableColumnKey, GenericTableCellValue> = {
      questionText: {
        type: "title",
        value: "Total Skills Used",
      },
    }
    const weekEnd = dailyWindowEnd
    getDayOrderForColumns(weekEnd).forEach((dayColumn) => {
      const dayDate = dayjs(dayColumn.key)
      const takeForDay = assessmentTakes.find((take) =>
        dayjs(take.completedAt).isSame(dayDate, "day")
      )
      if (takeForDay) {
        // count up the number of skills used (assuming skills questions have IDs in SkillsTableRowQuestionIDs)
        let skillCount = 0
        Object.values(SkillsTableRowQuestionIDs).forEach((questionId) => {
          const questionResult = takeForDay.results.find(
            (result) => result.questionId === questionId
          )
          if (questionResult) {
            const selectedOptions =
              questionResult.result as SwitchBoardQuestionResult[]
            skillCount += selectedOptions.filter((res) => res.value).length
          }
        })
        values[dayColumn.key] = {
          type: "overallSkillsRow",
          value: skillCount,
        }
      }
    })
    rows.push({
      id: "overallSkillsRow",
      values,
    })
    return { columns, rows, renderCell: CellRenderFunction }
  }, [DBTAssessment, assessmentTakes, dailyWindowEnd, ColumnDataForDailyDiary])

  /**
   * Generates the data for the skills table based on the skill name.
   * It creates rows based on the skills questions and the rolling daily window.
   * If the DBTAssessment or assessmentTakes are not loaded, it returns empty columns and rows.
   */
  const GenerateSkillsTableData = useCallback(
    (skillName: string): GenericTableProps => {
      if (!DBTAssessment) {
        return { columns: [], rows: [], renderCell: () => null }
      }
      const columns = ColumnDataForDailyDiary(skillName, "skillTitle")
      let rows: GenericTableRow[] = []
      let questionId = SkillsTableRowQuestionIDs[skillName]
      const question = DBTAssessment.pages
        .flatMap((page) => page.questions)
        .find((q) => q.id === questionId)
      if (question) {
        let values: Record<TableColumnKey, GenericTableCellValue> = {
          questionText: {
            type: "title",
            value: question.text,
          },
        }
        const weekEnd = dailyWindowEnd
        const dayOrder = getDayOrderForColumns(weekEnd)
        // These rows are based on the results for the question so we will need to get the question structure and expand that into rows
        const offset = SkillsTableRowOffsets[skillName]
        const skillQuestionOptions = (skillsQuestionsMap[questionId]?.properties
          ?.options ?? []) as SwitchboardOption[]
        const rowTitles = skillQuestionOptions.map(
          (option) =>
            `${option.index + offset}. ${option.title}${
              option.subtitle ? ` : ${option.subtitle}` : ""
            }`
        )
        skillQuestionOptions.forEach((option, rowIndex) => {
          // for each day in the week, find the corresponding assessment take
          // get the question result for that day
          dayOrder.forEach((dayColumn) => {
            const dayDate = dayjs(dayColumn.key)
            const takeForDay = assessmentTakes.find((take) =>
              dayjs(take.completedAt).isSame(dayDate, "day")
            )
            if (takeForDay) {
              const questionResult = takeForDay.results.find(
                (result) => result.questionId === questionId
              )
              if (questionResult) {
                const selectedOptions =
                  questionResult.result as SwitchBoardQuestionResult[]
                const matchedOption = selectedOptions.find(
                  (res) => res.index === option.index
                )
                values[dayColumn.key] = createYesNoBooleanCell(
                  matchedOption ? matchedOption.value : false
                )
              }
            }
          })
          rows.push({
            id: `row-${rowIndex}`,
            values: {
              ...values,
              questionText: {
                type: "titleWithColon",
                value: rowTitles[rowIndex],
              },
            },
          })
        })
      }
      return { columns, rows, renderCell: CellRenderFunction }
    },
    [
      DBTAssessment,
      assessmentTakes,
      dailyWindowEnd,
      ColumnDataForDailyDiary,
      skillsQuestionsMap,
    ]
  )

  /**
   * Generates the data for the Mindfulness skills table.
   */
  const MindfulnessSkillsTableData = useMemo<GenericTableProps>(
    () => GenerateSkillsTableData("Mindfulness"),
    [GenerateSkillsTableData]
  )

  /**
   * Generates the data for the Distress Tolerance skills table.
   */
  const DistressToleranceSkillsTableData = useMemo<GenericTableProps>(
    () => GenerateSkillsTableData("Distress Tolerance"),
    [GenerateSkillsTableData]
  )

  /**
   * Generates the data for the Walking The Middle Path skills table.
   */
  const WalkingTheMiddlePathSkillsTableData = useMemo<GenericTableProps>(
    () => GenerateSkillsTableData("Walking The Middle Path"),
    [GenerateSkillsTableData]
  )

  /**
   * Generates the data for the Emotional Regulation skills table.
   */
  const EmotionalRegulationSkillsTableData = useMemo<GenericTableProps>(
    () => GenerateSkillsTableData("Emotional Regulation"),
    [GenerateSkillsTableData]
  )

  /**
   * Generates the data for the Interpersonal Effectiveness skills table.
   */
  const InterpersonalEffectivenessSkillsTableData = useMemo<GenericTableProps>(
    () => GenerateSkillsTableData("Interpersonal Effectiveness"),
    [GenerateSkillsTableData]
  )

  return {
    DBTAssessment,
    loading:
      assessmentQuery.isLoading ||
      weeklyAssessmentsQuery.isLoading ||
      dailyAssessmentsQuery.isLoading ||
      skillsQuestionsQuery.isLoading,
    isDailyWindowLoading: dailyAssessmentsQuery.isFetching,
    error:
      assessmentQuery.error ??
      weeklyAssessmentsQuery.error ??
      dailyAssessmentsQuery.error ??
      skillsQuestionsQuery.error,
    UrgesAndBehaviorTableData,
    WeeklyCheckinTableData,
    EmotionsTableData,
    UrgesAndEmotionsSplitTableData: UrgesAndEmotionsSplitTableData,
    OverallSkillsTableData,
    MindfulnessTableData: MindfulnessSkillsTableData,
    DistressToleranceTableData: DistressToleranceSkillsTableData,
    WalkingTheMiddlePathTableData: WalkingTheMiddlePathSkillsTableData,
    EmotionalRegulationTableData: EmotionalRegulationSkillsTableData,
    InterpersonalEffectivenessTableData:
      InterpersonalEffectivenessSkillsTableData,
    hasDailyAssessments: assessmentTakes.length > 0,
    weekPageItems,
    selectedWeekKey: activeWeekKey,
    onWeekPageChange: setSelectedWeekKey,
    latestWeeklyCheckinCompletedAt: selectedWeeklyAssessment?.completedAt,
    hasWeeklyCheckin: Boolean(selectedWeeklyAssessment),
    hasWeeklyAssessments: weeklyAssessments.length > 0,
  }
}

export default useDBTDiaryOverview
